import React from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import _ from 'underscore';
import { Cascader } from '@bingads-webui-react/cascader';
import { withLiveRegionProp } from '@bingads-webui-react/hoc-with-live-region';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';
import { CampaignItemComponent } from './components/campaign-item';
import { AdgroupItemComponent } from './components/adgroup-item';

const CAMPAIGN_KEY = 'campaign';
const ADGROUP_KEY = 'adgroup';
const CAMPAIGN_PAGESIZE = 10;
const ADGROUP_PAGESIZE = 6;

const SELECTED_MODE = 0;
const SEARCH_MODE = 1;

/**
 * A business component of campaign adgroup selector based on cascader
 * Provide a component with funtion of selecting an item from some campaigns and adgroups
 */

const CascaderWithLiveRegion = withLiveRegionProp(Cascader);

export class OriginCampaignAdgroupSelector extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      campaign: {
        onFetch: () => { },
        onSelect: () => { },
        selectedItem: null,
        items: [],
        totalCount: 0,
        pageSize: CAMPAIGN_PAGESIZE,
        order: 0,
        ariaLabel: '',
      },
      adgroup: {
        onFetch: () => { },
        onSelect: () => { },
        selectedItem: null,
        items: [],
        totalCount: 0,
        pageSize: ADGROUP_PAGESIZE,
        order: 1,
        ariaLabel: '',
      },
    };
    this.adgroupFetchMode = SELECTED_MODE;
  }

  componentDidMount() {
    this.initOptions();
  }

  /**
   * @param {string} query - search query,
   * @returns {undefined} call the selector fetch with logic above
   */
  onSearch = (query) => {
    const { campaign, adgroup } = this.state;
    this.adgroupFetchMode = SEARCH_MODE;
    campaign.onFetch({ query });
    adgroup.onFetch({ query, scopeParentItem: campaign.selectedItem });
  }

  /**
   * fetch the campaign items
   * then update state
   * @param {string} query - search query,
   * @param {number} pageNumber - fetch page number,
   * @param {Object} scopeParentItem - the fetch paren scope
   * @returns {undefined} update the state with fetch result
   */
  onCampaignFetch = ({
    query = '', pageNumber = 0, scopeParentItem = null,
  } = {}) => {
    const { pageSize } = this.state.campaign;
    Promise.resolve(this.props.campaign.onFetch({
      query, pageNumber, pageSize, scopeParentItem,
    }))
      .then((data) => {
        if (data && data.items) {
          this.updateOptionItems(CAMPAIGN_KEY, data.items, data.totalCount);
        }
      });
  }

  /**
   * fetch the adgroup items
   * then update state
   * there is a business logic here:
   * when search clicked with search query:
   * adgroup fetch will in a search mode and ignore the scopeParentItem
   * when the campaign item clicked, adgroup fetch will in a selected mode and ignore the query
   * when both the query and scopeParentItem are empty, the adgroup will not fetch, should be empty
   * @param {string} query - search query,
   * @param {number} pageNumber - fetch page number,
   * @param {Object} scopeParentItem - the fetch paren scope
   * @returns {undefined} update the state with fetch result
   */
  onAdgroupFetch = ({
    query = '', pageNumber = 0, scopeParentItem = null,
  } = {}) => {
    if (!query && !scopeParentItem) {
      this.updateOptionItems(ADGROUP_KEY, [], 0);
      return;
    }
    let queryByMode = query;
    let scopeParentItemByMode = scopeParentItem;
    if (this.adgroupFetchMode === SELECTED_MODE) {
      queryByMode = '';
      scopeParentItemByMode = scopeParentItem;
    } else if (this.adgroupFetchMode === SEARCH_MODE && query) {
      queryByMode = query;
      scopeParentItemByMode = null;
    }
    const { pageSize } = this.state.adgroup;
    Promise.resolve(this.props.adgroup.onFetch({
      query: queryByMode, pageNumber, pageSize, scopeParentItem: scopeParentItemByMode,
    }))
      .then((data) => {
        if (data && data.items) {
          this.updateOptionItems(ADGROUP_KEY, data.items, data.totalCount);
        }
      });
  }

  /**
   * will update the option's selectedItem after selected
   * and call the props function to notify outter component
   * @param {Object} selectedItem - the item clicked
   * @returns {undefined} will update the state
   */
  onCampaignSelected = (selectedItem) => {
    this.props.campaign.onSelect(selectedItem);
    this.updateSelectedItems(CAMPAIGN_KEY, selectedItem);
    this.adgroupFetchMode = SELECTED_MODE;
  }

  /**
   * will update the option's selectedItem after selected
   * and call the props function to notify outter component
   * and toggle the dropdown
   * @param {Object} selectedItem - the item clicked
   * @returns {undefined} will update the state
   */
  onAdgroupSelected = (selectedItem) => {
    this.props.adgroup.onSelect(selectedItem);
    this.updateSelectedItems(ADGROUP_KEY, selectedItem);
    if (this.props.isMultiMode) {
      this.props.resultContainerOptions.updateResultList([selectedItem]);
    } else {
      this.props.dropdownOptions.toggle();
      this.props.dropdownOptions.updatedDropdownItem(selectedItem);
    }
  }

  /**
   * will update the select result container with all items in current pageafter click
   * @returns {undefined} will call the props callback function
   */
  onSelectAllClick = () => {
    this.props.resultContainerOptions.updateResultList(this.state.adgroup.items);
  }

  /**
   * will do the initial fetch
   * in this case, it will call the campaign fetch witn no params( use default params)
   * @returns {undefined} call campaign fetch
   */
  initFetch() {
    this.onCampaignFetch();
    if (this.props.selectedCampaign) {
      this.onAdgroupFetch({ scopeParentItem: this.props.selectedCampaign });
    }
  }

  /**
   * will init the campaign and adgroup options with the initial value and update state
   * when the component mount
   * @returns {undefined} will update the option state
   */
  initOptions() {
    const {
      i18n, campaign, adgroup, selectedAdGroup, selectedCampaign,
      isMultiMode, selectedAdgroupsForMultiMode, stringKeys,
    } = this.props;

    const campaignSelectorOption = {
      onFetch: this.onCampaignFetch,
      renderHeader: () => this.renderHeader(stringKeys && stringKeys.campaignListHeader ?
        i18n.getString(stringKeys.campaignListHeader, { itemCount: this.state.campaign.totalCount })
        : i18n.getString(_TL_('Campaigns: {{itemCount}}'), { itemCount: this.state.campaign.totalCount })),
      renderEmptyList: this.renderEmptyList,
      renderItem: this.renderCampaignItem,
      onSelect: this.onCampaignSelected,
      selectedItem: selectedCampaign,
      items: [],
      totalCount: 0,
      pageSize: campaign.pageSize ? campaign.pageSize : CAMPAIGN_PAGESIZE,
      order: 0,
      checkSelected: campaign.checkSelected,
      ariaLabel: stringKeys && stringKeys.campaignEntities ?
        i18n.getString(stringKeys.campaignEntities)
        : i18n.getString(_TL_('Campaigns')),
      selectorClassName: campaign.containerClassName,
    };

    const adGroupSelectorOption = {
      onFetch: this.onAdgroupFetch,
      renderHeader: () => this.renderHeader(stringKeys && stringKeys.adgroupListHeader ?
        i18n.getString(stringKeys.adgroupListHeader, { itemCount: this.state.adgroup.totalCount })
        : i18n.getString(_TL_('Available ad groups: {{itemCount}}'), { itemCount: this.state.adgroup.totalCount })),
      renderEmptyList: this.renderEmptyList,
      renderItem: this.renderAdgroupItem,
      renderActionBar: isMultiMode ? this.renderActionBar : undefined,
      onSelect: this.onAdgroupSelected,
      selectedItem: selectedAdGroup,
      items: [],
      totalCount: 0,
      pageSize: adgroup.pageSize ? adgroup.pageSize : ADGROUP_PAGESIZE,
      order: 1,
      checkSelected: adgroup.checkSelected,
      ariaLabel: stringKeys && stringKeys.adgroupEntities ?
        i18n.getString(stringKeys.adgroupEntities)
        : i18n.getString(_TL_('Ad groups')),
      selectorClassName: adgroup.containerClassName,
    };

    this.setState({
      campaign: campaignSelectorOption,
      adgroup: adGroupSelectorOption,
    }, () => {
      this.initFetch();
    });

    if (isMultiMode && !_.isEmpty(selectedAdgroupsForMultiMode)) {
      this.props.resultContainerOptions.updateResultList(selectedAdgroupsForMultiMode);
    }
  }

  /**
   * update the state options after fetch
   * @param {string} itemKey - update which options: campaign or adgroup,
   * @param {Object[]} items - items list
   * @param {number} totalCount - item total count
   * @returns {undefined} update the option state
   */
  updateOptionItems(itemKey, items, totalCount) {
    const option = this.state[itemKey];
    option.items = items;
    option.totalCount = totalCount;
    this.setState({ itemKey: option });
  }

  /**
   * update the state options after selected
   * @param {string} itemKey - update which options: campaign or adgroup,
   * @param {Object} selectedItem - selected item
   * @returns {undefined} update the option state
   */
  updateSelectedItems(itemKey, selectedItem) {
    const option = this.state[itemKey];
    option.selectedItem = selectedItem;
    this.setState({ itemKey: option });
  }

  /**
   * transform the adgroup item with the isAddedToResultList property
   * @returns {object} the transformed AdgroupOption
   */
  adgroupItemAdapter() {
    const { resultContainerOptions, isMultiMode } = this.props;
    const { items } = this.state.adgroup;
    const adgroupAdapter = [...items];

    for (let i = 0; i < items.length; i += 1) {
      if (isMultiMode) {
        adgroupAdapter[i].isShowAddArrow = true;
      }
      if (this.findItemIndex(resultContainerOptions.resultList, items[i]) > -1) {
        adgroupAdapter[i].isAddedToResultList = true;
      } else {
        adgroupAdapter[i].isAddedToResultList = false;
      }
    }

    const AdgroupOption = this.state[ADGROUP_KEY];
    AdgroupOption.items = adgroupAdapter;
    return AdgroupOption;
  }

  getScreenReaderText() {
    const { i18n } = this.props;

    return `${i18n.getString(_TL_('Campaigns: {{itemCount}}'), { itemCount: this.state.campaign.totalCount })}; ${i18n.getString(_TL_('Available ad groups: {{itemCount}}'), { itemCount: this.state.adgroup.totalCount })}`;
  }

  /**
    * util function: find if an item is in the list(same Id), if so, return the index
    * @param {list} list - the list to find the item
    * @param {Object} item - item to be found
    * @returns {number} index, if not found, will return -1
    */
  findItemIndex(list, item) {
    for (let i = 0; i < list.length; i += 1) {
      if (item.Id === list[i].Id) {
        return i;
      }
    }
    return -1;
  }

  /**
   * render component for the selector header
   * @param {string} header - header tips to show
   * @returns {object} return the header component
   */
  renderHeader = header => (
    <div className="panel-heading">
      <h4 className="panel-title">
        {header}
      </h4>
    </div>
  )

  /**
   * render component for the empty selector
   * @returns {object} return the empty component
   */
  renderEmptyList = () => {
    const { stringKeys } = this.props;
    return (
      <div className="empty-content" role="option" aria-selected="false">
        {stringKeys && stringKeys.emptyListContent ?
          this.props.i18n.getString(stringKeys.emptyListContent)
        : this.props.i18n.getString(_TL_('No results to display.'))}
      </div>
    );
  }

  /**
   * render campaign item component
   * @param {object} item - campaign item
   * @param {number} index - current item index
   * @returns {object} return the campaign item component
   */
  renderCampaignItem = (item, index) => (<CampaignItemComponent item={item} index={index} />);

  /**
   * render adgroup item component
   * @param {object} item - adgroup item
   * @param {number} index - current item index
   * @returns {object} return the adgroup item component
   */
  renderAdgroupItem = (item, index) => (<AdgroupItemComponent item={item} index={index} />);

  /**
   * render action bar: select all
   * @returns {object} return the action bar component
   */
  renderActionBar = () => {
    const { stringKeys } = this.props;
    return (
      <div
        className="select-all-content"
        onClick={this.onSelectAllClick}
        onKeyDown={submitEvent(() => this.onSelectAllClick())}
        role="menuitem"
        tabIndex={0}
      >
        <span>{stringKeys && stringKeys.actionBar ?
            this.props.i18n.getString(stringKeys.actionBar)
          : this.props.i18n.getString(_TL_('Select all on this page'))}
        </span>
        <span className="enabled arrow-button" />
      </div>
    );
  }

  render() {
    const { i18n, i18nLegacy, isMultiMode } = this.props;
    const adgroup = isMultiMode ? this.adgroupItemAdapter() : this.state.adgroup;
    const cascaderOptions = [this.state.campaign, adgroup];

    return (
      <CascaderWithLiveRegion
        cascaderOptions={cascaderOptions}
        onSearch={this.onSearch}
        isShowSearchbox
        i18n={i18n}
        i18nLegacy={i18nLegacy}
        enableArrowKeysSupport
        aria-live="assertive"
        screenReaderText={this.getScreenReaderText()}
      />
    );
  }
}

OriginCampaignAdgroupSelector.propTypes = {

  campaign: PropTypes.shape({
    onFetch: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    pageSize: PropTypes.number,
    checkSelected: PropTypes.func,
  }).isRequired,

  adgroup: PropTypes.shape({
    onFetch: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    pageSize: PropTypes.number,
    checkSelected: PropTypes.func,
  }).isRequired,

  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,

  i18nLegacy: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }),
  stringKeys: PropTypes.objectOf(PropTypes.string),
  selectedCampaign: PropTypes.objectOf(PropTypes.any),
  selectedAdGroup: PropTypes.objectOf(PropTypes.any),
  isMultiMode: PropTypes.bool,
  selectedAdgroupsForMultiMode: PropTypes.arrayOf(PropTypes.any),
  resultContainerOptions: PropTypes.shape({
    resultList: PropTypes.arrayOf(PropTypes.any),
    updateResultList: PropTypes.func,
  }),
  dropdownOptions: PropTypes.shape({
    toggle: PropTypes.func,
    updatedDropdownItem: PropTypes.func,
  }),
};

OriginCampaignAdgroupSelector.defaultProps = {
  selectedCampaign: null,
  selectedAdGroup: null,
  stringKeys: null,
  isMultiMode: false,
  selectedAdgroupsForMultiMode: [],
  resultContainerOptions: {
    resultList: [],
    updateResultList: _.noop,
  },
  dropdownOptions: {
    toggle: _.noop,
    updatedDropdownItem: _.noop,
  },
  i18nLegacy: null,
};
